import config, { IS_WEB } from "../config";
import { Device, Preferences } from "../plugins";
import { THEME_CONFIG } from "../style";
import CryptoJS from "crypto-js";
import cachedFirestore from "../cachedFirestore";
import fieldsDef from "./fieldsDef";
import _ from "lodash";
import { yieldWithPriority } from "../promise";
import { catchErrorWithStack } from "../crashlytics";
import { getLogger } from "../logging";
import { getEpisodeIdx } from "../levels/episodes";

const log = getLogger("📄 Store");

const PASSPHRASE = "awesomegame";
const ENCRYPT = !__DEV__;
// const ENCRYPT = true;

const safeLocalStorageGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    log.error(`Failed to get item '${key}' from localStorage`, e);
    return null;
  }
};

const safeLocalStorageSetItem = (key, value) => {
  try {
    return localStorage.setItem(key, value);
  } catch (e) {
    log.error(`Failed to set item '${key}': '${value}' to localStorage`, e);
    return null;
  }
};
const preferencesGetItem = async (key) => {
  try {
    const { value } = await Preferences.get({ key });
    return value;
  } catch (e) {
    log.error(`Failed to get item '${key}' from Preferences`, e);
    return null;
  }
};

class Store {
  async initialize() {
    await Preferences.configure({
      group: "Store",
    });
    if (
      safeLocalStorageGetItem("appVersion") !== null &&
      safeLocalStorageGetItem("_migratedToPreferences") === null
    ) {
      log.info("📄 Migrating from localStorage to Preferences");
      await this.#initFrom(safeLocalStorageGetItem);
    } else
      await this.#initFrom(async (key, noEncrypt) => {
        let value = await preferencesGetItem(key);
        if (ENCRYPT && !noEncrypt) value = value && this.#decryptValue(value);
        return value;
      });
    await this.postInit();
    this.themes = THEME_CONFIG; // todo: remove this
    this.secondsPassed = 0;
    const { getEpisodeIdx } = await require("../levels/episodes");
    this.episodeIdx = getEpisodeIdx(this.levelIdx);
    this.screenNamePrev = null;
    this.gameFrames = [];
    this.gameFramesReplaying = [];
    this.step = 0;
    this.gameCount = 0;
    this.internetConnected =
      this.internetConnected === undefined ? true : this.internetConnected;
    this.requestsTimingOut =
      this.requestsTimingOut === undefined ? false : this.requestsTimingOut;
    this.innerHeight = window.innerHeight;
    this.innerWidth = window.innerWidth;
    this.ar = this.innerWidth / this.innerHeight;
    this.languageCode = getLanguageCode();
    this.isUpdate = false;

    if (!IS_WEB) {
      if (config.VERSION != this.appVersion) {
        if (this.appVersion != "") {
          log.info(`Updated from ${this.appVersion} to ${config.VERSION}`);
          this.isUpdate = true;
        } else if (this.maxLevelIdx) {
          log.info(`Updated from an old version to ${config.VERSION}`);
          this.appVersion = `<${config.VERSION}`;
          this.isUpdate = true;
        } else
          log.info(`First time running app with version ${config.VERSION}`);
        this.set("maxLevelIdxVersionPrev", this.maxLevelIdx);
        this.set("appVersionPrev", this.appVersion);
        this.set("appVersion", config.VERSION);
      }
    } else IS_WEB || log.error("getAppVersion not found");
    await this.saveAll();
  }

  #initFrom(getter) {
    return Promise.all(
      Object.values(fieldsDef).map(
        async ({ key, defaultValue, parser, noEncrypt }) => {
          try {
            const value = await getter(key, noEncrypt);
            this[key] = parser(value);
          } catch (error) {
            catchErrorWithStack(error);
            log.error(
              `Invalid value ${this[key]} for ${key}, setting to ${defaultValue}`
            );
            this[key] = defaultValue;
            return;
          }
          if ([null, undefined].includes(this[key])) {
            log.warn(`Invalid value for ${key}, setting to ${defaultValue}`);
            this[key] = defaultValue;
          }
          if (
            typeof defaultValue == "number" &&
            (isNaN(this[key]) || this[key] == "NaN")
          ) {
            log.warn(`NaN value for ${key}, setting to ${defaultValue}`);
            this[key] = defaultValue;
          } else if (
            defaultValue instanceof Date &&
            isNaN(this[key].getTime())
          ) {
            log.warn(
              `Invalid date value ${this[key]} for ${key}, setting to ${defaultValue}`
            );
            this[key] = defaultValue;
          }
        }
      )
    );
  }

  #encryptValue(value) {
    try {
      value = JSON.stringify(value);
      const encryptedValue = CryptoJS.AES.encrypt(value, PASSPHRASE).toString();
      return encryptedValue;
    } catch (err) {
      log.error(`Failed to encrypt value '${value}'`, err);
      throw err;
    }
  }

  #decryptValue(encryptedValue) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedValue, PASSPHRASE);
      const decryptedValue = bytes.toString(CryptoJS.enc.Utf8);
      try {
        return JSON.parse(decryptedValue);
      } catch (error) {
        log.error(`Failed to parse value '${decryptedValue}'`);
        return decryptedValue;
      }
    } catch (err) {
      log.error(`Failed to decrypt value '${encryptedValue}'`);
    }
    return null;
  }

  async postInit() {
    await this.validateAndAdjustData();
    if (safeLocalStorageGetItem("_migratedToPreferences") === null)
      safeLocalStorageSetItem("_migratedToPreferences", true);
  }

  async validateAndAdjustData() {
    const { CLASSIC_LEVELS, LEVELS } = await require("../levels/definitions");
    this.themeIdx = 0; // todo: remove this
    this.maxLevelIdx = Math.min(this.maxLevelIdx, LEVELS.length);
    this.classicLevelIdx = _.clamp(
      this.classicLevelIdx,
      0,
      CLASSIC_LEVELS.length - 1
    );
    this.levelIdx = Math.min(this.levelIdx, this.maxLevelIdx);
    this.#adjustLivesForClosedWhilePlaying();
    this.numLives = Math.max(this.numLives, 0);
    this.interstitialAdsInterval = Math.max(this.interstitialAdsInterval, 1);
    this.scale = 1;
    for (let i = 0; i < getEpisodeIdx(this.maxLevelIdx); i++)
      this.difficultyFeedbacks.add(i);
  }

  #adjustLivesForClosedWhilePlaying() {
    if (this.screenName == "game") {
      log.debug("📄 Removing one live for closed while playing");
      this.numLives -= 1;
    }
    this.screenName = "main_menu";
  }

  async saveAll() {
    const fields = this.#getFields();
    await Promise.all(fields.map((key) => this.save(key, true)));
  }

  save = async (key, skipFirestore = false) => {
    await yieldWithPriority("background");
    const { saveToFirestore, noEncrypt } = fieldsDef[key] || {
      saveToFirestore: false,
      noEncrypt: true,
    };
    const value = this.#getStorableValue(this[key], key);
    const encryptedValue =
      ENCRYPT && !noEncrypt ? this.#encryptValue(value) : value;
    await Preferences.set({
      key,
      value: encryptedValue,
    });
    if (!saveToFirestore || skipFirestore) return;
    if (!this.user.uid) {
      log.debug("📄 No user, not saving to firestore");
      return;
    }
    const firestoreValue = this.#getFirestorableValue(this[key]);
    await cachedFirestore.setDocument({
      reference: `store/${this.user.uid}`,
      data: { [key]: firestoreValue },
      merge: true,
    });
  };

  #getStorableValue(value, key) {
    let storableValue = value;
    if (value === undefined || value === null) {
      log.error(`Undefined or null value for key '${key}' encountered`);
      storableValue = "";
    } else if (typeof value.toString !== "function") {
      log.error(`No toString() function for key '${key}' encountered`);
      storableValue = "";
    }
    if (value instanceof Set) storableValue = JSON.stringify([...value]);
    else if (value instanceof Date) storableValue = value;
    else if (value instanceof Object) storableValue = JSON.stringify(value);
    else value = storableValue.toString();
    return storableValue;
  }

  #getFirestorableValue(value) {
    if (value instanceof Set) return [...value];
    return value;
  }

  async saveAllToFirestore() {
    if (!this.user.uid) {
      log.debug("📄 No user, not saving to firestore");
      return;
    }
    const fields = Object.values(fieldsDef)
      .filter(({ saveToFirestore }) => saveToFirestore)
      .map(({ key }) => key);
    const data = fields.reduce((acc, key) => {
      acc[key] = this.#getFirestorableValue(this[key]);
      return acc;
    }, {});
    await cachedFirestore.setDocument({
      reference: `store/${this.user.uid}`,
      data,
      merge: true,
    });
  }

  async set(key, value, skipFirestore = false) {
    if (value === undefined)
      log.warn(`Setting undefined value for key '${key}'`);
    this[key] = value;
    await this.save(key, skipFirestore);
  }

  #getFields(includeAll = false) {
    if (includeAll)
      return Object.keys(this).filter((key) => typeof this[key] !== "function");
    return Object.keys(fieldsDef);
  }

  getAll(includeAll = false) {
    return this.#getFields(includeAll).reduce((acc, key) => {
      acc[key] = this[key];
      return acc;
    }, {});
  }

  getAllAsStorable(includeAll = false) {
    const all = this.getAll(includeAll);
    return Object.entries(all).reduce((acc, [key, value]) => {
      acc[key] = this.#getStorableValue(value, key);
      return acc;
    }, {});
  }

  reset() {
    if (!__DEV__) throw new Error("📄 Cannot reset store in production");
    Object.values(fieldsDef).forEach(({ key, defaultValue }) => {
      this[key] = defaultValue;
      this.save(key);
    });
  }

  mergeStore(otherStore) {
    Object.values(fieldsDef).forEach(({ key, firestoreParser }) => {
      if (otherStore[key] !== undefined) {
        let value = otherStore[key];
        if (typeof value === "number" && !isNaN(value))
          value = Math.max(this[key], otherStore[key]);
        else
          try {
            value = firestoreParser
              ? firestoreParser(otherStore[key])
              : otherStore[key];
          } catch (error) {
            log.error(
              `Merging ${key}: ${this[key]} -> parse(${otherStore[key]}) failed`
            );
            catchErrorWithStack(error);
            return;
          }
        if (!_.isEqual(this[key], value)) {
          log.debug(`Merging ${key}: ${this[key]} -> ${value}`);
          this.set(key, value);
        }
      }
    });
  }

  filterFirestorable(otherStore) {
    return Object.entries(otherStore).reduce((acc, [key, value]) => {
      if (fieldsDef[key] && fieldsDef[key].saveToFirestore) acc[key] = value;
      return acc;
    }, {});
  }
}

function getLanguageCode() {
  //   if (__DEV__) return "de";
  const language = navigator.language || navigator.userLanguage;
  if (language.startsWith("zh")) {
    if (["zh-cn", "zh-tw"].contains(language)) return language;
    return "zh-cn";
  }
  const languageCode = language.slice(0, 2);
  return languageCode;
}

const store = new Store();
Device.getId().then(({ identifier }) => (store.deviceId = identifier));

if (__DEV__ || __IFRAME__) window.store = store;
export default store;
